import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Inject, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { CalendarEventTypes } from '@core/enums/calendar-event-type.enum';
import { Banks } from '@core/enums';
import { CaseDetails, LeaveInfo, SelectionOption } from '@core/models';
import { EmployeeLeaveHoursRequests, CustomCalendarEventProps, EmployeeLeaveHoursExtended, WorkCalendarDay } from '@core/models/leave-admin/leave-calendar/leave-calendar.model';
import { LayoutService } from '@core/services';
import { ReviewRequestsService } from '@core/services/review-requests.service';
import { EventApi } from '@fullcalendar/core';
import { indicate } from '@shared/helpers';
import { position, onDragEnded } from '@shared/helpers/dialog-drag-constraints';
import { Subject, Observable } from 'rxjs';
import { ReviewRequestDialogComponent, ReviewRequestDialogComponentData, ReviewRequestDialogMode } from '../review-request-dialog/review-request-dialog.component';
import { EmployeeRecord } from '@core/models/leave-admin/employees/employee-record.model';

export interface ViewCalendarItemComponentData { 
  event: EventApi;
  sysText: any;
  // supplying employee record indicates that the calendar item is
  // being opened from the employee calendar, otherwise the calendar
  // item is being opened from the case calendar
  employeeRecord: EmployeeRecord; //nullable, supplied with request for leave hours item from employee calendar
  caseDetails: CaseDetails; //nullable, supplied with request for leave hours item from employee or case calendar
  unsavedChangesSysText: any; //nullable, supplied with request for leave hours item from employee or case calendar
  reviewRequestSysText: any; //nullable, supplied with request for leave hours item from employee or case calendar
  leaveYearOptions: SelectionOption[]; //nullable, supplied for extended hours item
  workday: WorkCalendarDay; //nullable, supplied for extended hours item
}

// one leave hours bank's fields, read from the models by getBankFields
export interface BankFields {
  applicable: boolean;
  restrictToFullDays: boolean;
  name: string;
  caseStartDate: Date;
  caseEndDate: Date;
  caseEffectiveHours: number;
  caseUsedHours: number;
  caseFutureHours: number;
  leavesCount: number;
  drawCount: number;
  leaveYearId: number;
  leaveYearState: string;
  leaveYearDuration: number;
  leaveYearDurationTypeId: number;
  leaveYearStartId: number;
  startDate: Date;
  endDate: Date;
  employeeTotalHours: number;
  employeeUsedHours: number;
  employeeAdjustments: number;
  employeeAvailableHours: number;
  employeeFutureHours: number;
}

// one leave hours bank's figures for the day, as the dialog shows them
export interface BankSummary extends BankFields {
  casePeriod: string;
  fullDayWarn: boolean;
  leaveYear: string;
  period: string;
  exhausted: boolean;
  overdrawn: boolean;
}

@Component({
    selector: 'app-view-calendar-item',
    templateUrl: './view-calendar-item.component.html',
    styleUrls: ['./view-calendar-item.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ViewCalendarItemComponent implements OnInit {
  static readonly componentName = 'ViewCalendarItemComponent';
  
  @Output() isEditing = new EventEmitter<boolean>();
  @Output() reviewClosed = new EventEmitter<void>();

  public calendarEventTypes = CalendarEventTypes;
  public leaveInformation: LeaveInfo;
  public leaveYearOptions: SelectionOption[];
  public employeeLeaveHoursExtended: EmployeeLeaveHoursExtended;
  public employeeLeaveHoursRequests: EmployeeLeaveHoursRequests[];
  public eventProps: CustomCalendarEventProps;
  public isSubmitting$: Subject<boolean> = new Subject<boolean>();
  public restrictHoursToWorkdays: boolean;
  public restrictHoursToWorkdayHours: boolean;
  public titleHeader: string;
  // the day's summary of hours and banks (the other event types show only their own details)
  public isDaySummary: boolean;
  public canReviewRequest: boolean;
  // the banks this day draws from, in order, and their state for the title and the case's Draw row. The dialog's
  // data doesn't change while it's open, so they're worked out once
  public bankSummaries: BankSummary[];
  public exhausted: boolean;
  public overdrawn: boolean;
  public drawBanks: string;

  public position = position;
  public onDragEnded = onDragEnded;

  constructor (
    @Inject(MAT_DIALOG_DATA)
    public data: ViewCalendarItemComponentData,
    private datePipe: DatePipe,
    private layoutService: LayoutService,
    private dialogRef: MatDialogRef<ViewCalendarItemComponent>,
    private reviewRequestService: ReviewRequestsService,
    private dialog: MatDialog
  ) {
    this.leaveInformation = this.data.caseDetails?.leaveInformation;
    this.leaveYearOptions = this.data.leaveYearOptions;
    this.eventProps = this.data.event.extendedProps?.customProps as CustomCalendarEventProps;
    this.employeeLeaveHoursExtended = this.eventProps?.employeeLeaveHoursExtended;
    this.employeeLeaveHoursRequests = this.eventProps?.employeeLeaveHoursRequests;
    this.restrictHoursToWorkdays = this.eventProps?.restrictHoursToWorkdays;
    this.restrictHoursToWorkdayHours = this.eventProps?.restrictHoursToWorkdayHours;
    this.titleHeader = this.getTitleHeader();

    const eventType = this.eventProps?.eventType;
    this.isDaySummary = eventType != CalendarEventTypes.ActionItem
      && eventType != CalendarEventTypes.HoursWorked
      && eventType != CalendarEventTypes.RequestForLeaveHours;

    this.canReviewRequest = eventType == CalendarEventTypes.RequestForLeaveHours
      && this.data.caseDetails?.canReviewRequestForHours == true;

    this.bankSummaries = this.getBankFields()
      .filter(bank => bank.applicable)
      .map(bank => this.getBankSummary(bank));

    this.exhausted = this.leaveInformation?.leaveTypeRequireAllBankHours
      ? this.bankSummaries.some(bank => bank.exhausted)
      : this.bankSummaries.length > 0 && this.bankSummaries.every(bank => bank.exhausted);

    this.overdrawn = this.bankSummaries.some(bank => bank.overdrawn);
    
    this.drawBanks = this.bankSummaries.length > 0
      ? this.bankSummaries.map(bank => bank.name).join(', ')
      : null;
  }

  ngOnInit(): void {
    this.isMobile$ = this.layoutService.isMobile$();
  }

  public isMobile$: Observable<boolean>;

  public close(): void {
    this.dialogRef.close();
  }
  
  public hasCaseHoursToday(): boolean {
    return this.employeeLeaveHoursExtended?.leaveCaseHoursID != null && this.leaveInformation?.hiddenDateTime == null;
  }

  public caseIsHidden(): boolean {
    if (this.employeeLeaveHoursExtended?.leaveCaseHoursID == null)
      return false;

    return this.leaveInformation?.hiddenDateTime != null;
  }

  public outsideCaseTimeframe(): boolean {
    if (this.employeeLeaveHoursExtended?.leaveCaseHoursID == null)
      return false;

    let calendarDateFormed = this.employeeLeaveHoursExtended?.calendarDate
      ? this.datePipe.transform(new Date(this.employeeLeaveHoursExtended?.calendarDate), "yyyy-MM-dd")
      : null;

    let outsideTimeframe = 
      this.leaveInformation?.startDate == null
      || this.leaveInformation?.endDate == null
      || calendarDateFormed < this.datePipe.transform(this.leaveInformation?.startDate, "yyyy-MM-dd") 
      || calendarDateFormed > this.datePipe.transform(this.leaveInformation?.endDate, "yyyy-MM-dd");

      return outsideTimeframe;
  }

  public notWorkday(): boolean {
    if (this.employeeLeaveHoursExtended?.leaveCaseHoursID == null)
      return false;

    if (this.outsideCaseTimeframe())
      return false;

    if (!this.restrictHoursToWorkdays)
      return false;

    let notWorkday = this.data.workday?.includeActualSourceHours != true;

    return notWorkday && this.data.workday?.includeNonWorkday != true;
  }

  public exceedsWorkdayHours(): boolean {
    if (this.employeeLeaveHoursExtended?.leaveCaseHoursID == null)
      return false;

    if (this.outsideCaseTimeframe() || this.notWorkday())
      return false;

    if (!this.restrictHoursToWorkdayHours)
      return false;

    let exceedsHours = this.employeeLeaveHoursExtended.leaveCaseLeaveHours > this.data.workday?.actualSourceHours;

    return exceedsHours;
  }

  public period(startDate: Date, endDate: Date): string {
    if (startDate == null || endDate == null)
      return null;

    return `${this.datePipe.transform(startDate, "mediumDate")} - ${this.datePipe.transform(endDate, "mediumDate")}`;
  }

  public getLeaveYearName(
    leaveYearId: number,
    leaveYearState: string,
    leaveYearDuration: number,
    leaveYearDurationTypeId: number,
    leaveYearStart: number): string {
    if (leaveYearId == null) {
      return null;
    }

    // format is "[leaveYearState (if supplied)] [leaveYear] - [leaveYearDuration] [leaveYearDurationType] - [leaveYearStart (if supplied)]
    let fullLeaveYearName: string = "";
    const leaveYear = this.leaveYearOptions?.find(ly => ly.id == leaveYearId)?.description;
    if (!leaveYear) {
      return leaveYearId.toString();
    }

    // start with state if supplied
    if (leaveYearState){
      fullLeaveYearName += `${leaveYearState} `;
    }
    fullLeaveYearName += leaveYear;

    // next add duration and duration type
    if (leaveYearDuration != null && leaveYearDurationTypeId != null) {
      let durationType: string = this.data.sysText["durationTypes_" + leaveYearDurationTypeId];
      if (leaveYearDuration === 1 && durationType.endsWith("s")) {
        durationType = durationType.slice(0,-1);
      }
      fullLeaveYearName += ` - ${leaveYearDuration} ${durationType}`;

      // then add measured forward start if supplied
      if (leaveYearStart != null) {
        fullLeaveYearName += ` - ${this.data.sysText["measuredForwardStart_" + leaveYearStart]}`;
      }
    }
    
    return fullLeaveYearName;
  }

  public getThisDatePendingRequests(): string {
    if (this.employeeLeaveHoursRequests?.length > 0) {
      // all leave hours request objects for this date contain the total 
      // (thisDateTotalRequestCount) so just grab value from the first one
      return this.employeeLeaveHoursRequests[0].thisDateTotalRequestCount.toString();
    } else {
      return null;
    }
  }

  public getThisDatePendingHours(): string {
    if (this.employeeLeaveHoursRequests?.length > 0) {
      // all leave hours request objects for this date contain the total 
      // (thisDateTotalRequestedHours) so just grab value from the first one
      return this.employeeLeaveHoursRequests[0].thisDateTotalRequestedHours.toString();
    } else {
      return null;
    }
  }

  public getThisCasePendingHours(): string {
    if (this.employeeLeaveHoursRequests?.length > 0) {
      return this.employeeLeaveHoursRequests.reduce((sum, req) => sum + req.hours, 0).toString();
    } else {
      return null;
    }
  }

  public onReviewRequest(): void {
    const item = this.employeeLeaveHoursRequests[0];
    this.reviewRequestService.getReviewRequestDialog(item.leaveHoursRequestId, item.caseId)
      .pipe(indicate(this.isSubmitting$))
      .subscribe((res) => {
        const dialogRef = this.dialog.open<ReviewRequestDialogComponent, ReviewRequestDialogComponentData>(ReviewRequestDialogComponent, {
          id: ReviewRequestDialogComponent.componentName,
          width: '990px',
          panelClass: "mat-dialog-container-mobileWidth",
          data: { 
            mode: this.data.employeeRecord
              ? ReviewRequestDialogMode.EmployeeCalendar
              : ReviewRequestDialogMode.CaseCalendar, 
            requestItemId: item.leaveHoursRequestId,
            dialogData: res,
            sysText: this.data.reviewRequestSysText,
            unsavedChangesSysText: this.data.unsavedChangesSysText,
            currentCaseId: this.data.caseDetails?.leaveInformation.caseId,
            currentEmployeeId: this.data.employeeRecord?.employeeId
          }
        });

        dialogRef.componentInstance.isEditing.subscribe((res) => {
          this.isEditing.emit(res);
        });

        dialogRef.afterOpened().subscribe(() => {
            this.dialogRef.close();
        })
        
        dialogRef.afterClosed().subscribe(() => {
          this.isEditing.emit(false);
          // this dialog closed itself when the review opened, so the review can't return focus to its
          // Review request button; the calendar that opened this dialog moves focus instead
          this.reviewClosed.emit();
        });
      });
  }

  // the calendar opens this dialog for several kinds of event, so the title says which
  private getTitleHeader(): string {
    switch (this.eventProps?.eventType) {
      case CalendarEventTypes.ActionItem:
        return this.data.sysText.actionItem_header;
      case CalendarEventTypes.HoursWorked:
        return this.data.sysText.hoursWorked_header;
      case CalendarEventTypes.RequestForLeaveHours:
        return this.data.sysText.leaveHoursRequest_header;
      default:
        return this.data.sysText.calendarDaySummary_header;
    }
  }

  private getBankSummary(fields: BankFields): BankSummary {
    return {
      ...fields,
      casePeriod: this.period(fields.caseStartDate, fields.caseEndDate),
      fullDayWarn: fields.restrictToFullDays
        && fields.caseEffectiveHours != null
        && (!this.data.workday || fields.caseEffectiveHours != this.data.workday.fullDayIncrement),
      leaveYear: this.getLeaveYearName(
        fields.leaveYearId,
        fields.leaveYearState,
        fields.leaveYearDuration,
        fields.leaveYearDurationTypeId,
        fields.leaveYearStartId),
      period: this.period(fields.startDate, fields.endDate),
      exhausted: fields.caseEffectiveHours == null
        && fields.employeeAvailableHours != null
        && fields.employeeAvailableHours == 0,
      overdrawn: fields.employeeAvailableHours != null && fields.employeeAvailableHours < 0
    };
  }

  private getBankFields(): BankFields[] {
    const info = this.leaveInformation;
    const hours = this.employeeLeaveHoursExtended;
    const buckets = this.data.caseDetails?.leaveHoursBanksBuckets;

    // a Record over the Banks enum: the compiler requires exactly one entry per bank
    const banks: Record<Banks, BankFields> = {
      [Banks.Bank1]: {
        applicable: info?.isBank1HoursApplicable,
        restrictToFullDays: info?.bank1RestrictToFullDayIncrements,
        name: buckets?.bank1Name ?? buckets?.bank1DefaultName,
        caseStartDate: hours?.leaveCaseBank1StartDate,
        caseEndDate: hours?.leaveCaseBank1EndDate,
        caseEffectiveHours: hours?.leaveCaseBank1EffectiveHours,
        caseUsedHours: hours?.leaveCaseBank1UsedHours,
        caseFutureHours: hours?.leaveCaseBank1FutureHours,
        leavesCount: hours?.bank1LeavesCount,
        drawCount: hours?.bank1DrawCount,
        leaveYearId: hours?.bank1LeaveYearID,
        leaveYearState: hours?.bank1LeaveYearState,
        leaveYearDuration: hours?.bank1LeaveYearDuration,
        leaveYearDurationTypeId: hours?.bank1LeaveYearDurationTypeID,
        leaveYearStartId: hours?.bank1LeaveYearStartID,
        startDate: hours?.bank1StartDate,
        endDate: hours?.bank1EndDate,
        employeeTotalHours: hours?.employeeBank1TotalHours,
        employeeUsedHours: hours?.employeeBank1UsedHours,
        employeeAdjustments: hours?.employeeBank1Adjustments,
        employeeAvailableHours: hours?.employeeBank1AvailableHours,
        employeeFutureHours: hours?.employeeBank1FutureHours
      },
      [Banks.Bank2]: {
        applicable: info?.isBank2HoursApplicable,
        restrictToFullDays: info?.bank2RestrictToFullDayIncrements,
        name: buckets?.bank2Name ?? buckets?.bank2DefaultName,
        caseStartDate: hours?.leaveCaseBank2StartDate,
        caseEndDate: hours?.leaveCaseBank2EndDate,
        caseEffectiveHours: hours?.leaveCaseBank2EffectiveHours,
        caseUsedHours: hours?.leaveCaseBank2UsedHours,
        caseFutureHours: hours?.leaveCaseBank2FutureHours,
        leavesCount: hours?.bank2LeavesCount,
        drawCount: hours?.bank2DrawCount,
        leaveYearId: hours?.bank2LeaveYearID,
        leaveYearState: hours?.bank2LeaveYearState,
        leaveYearDuration: hours?.bank2LeaveYearDuration,
        leaveYearDurationTypeId: hours?.bank2LeaveYearDurationTypeID,
        leaveYearStartId: hours?.bank2LeaveYearStartID,
        startDate: hours?.bank2StartDate,
        endDate: hours?.bank2EndDate,
        employeeTotalHours: hours?.employeeBank2TotalHours,
        employeeUsedHours: hours?.employeeBank2UsedHours,
        employeeAdjustments: hours?.employeeBank2Adjustments,
        employeeAvailableHours: hours?.employeeBank2AvailableHours,
        employeeFutureHours: hours?.employeeBank2FutureHours
      },
      [Banks.Bank3]: {
        applicable: info?.isBank3HoursApplicable,
        restrictToFullDays: info?.bank3RestrictToFullDayIncrements,
        name: buckets?.bank3Name ?? buckets?.bank3DefaultName,
        caseStartDate: hours?.leaveCaseBank3StartDate,
        caseEndDate: hours?.leaveCaseBank3EndDate,
        caseEffectiveHours: hours?.leaveCaseBank3EffectiveHours,
        caseUsedHours: hours?.leaveCaseBank3UsedHours,
        caseFutureHours: hours?.leaveCaseBank3FutureHours,
        leavesCount: hours?.bank3LeavesCount,
        drawCount: hours?.bank3DrawCount,
        leaveYearId: hours?.bank3LeaveYearID,
        leaveYearState: hours?.bank3LeaveYearState,
        leaveYearDuration: hours?.bank3LeaveYearDuration,
        leaveYearDurationTypeId: hours?.bank3LeaveYearDurationTypeID,
        leaveYearStartId: hours?.bank3LeaveYearStartID,
        startDate: hours?.bank3StartDate,
        endDate: hours?.bank3EndDate,
        employeeTotalHours: hours?.employeeBank3TotalHours,
        employeeUsedHours: hours?.employeeBank3UsedHours,
        employeeAdjustments: hours?.employeeBank3Adjustments,
        employeeAvailableHours: hours?.employeeBank3AvailableHours,
        employeeFutureHours: hours?.employeeBank3FutureHours
      },
      [Banks.Bank4]: {
        applicable: info?.isBank4HoursApplicable,
        restrictToFullDays: info?.bank4RestrictToFullDayIncrements,
        name: buckets?.bank4Name ?? buckets?.bank4DefaultName,
        caseStartDate: hours?.leaveCaseBank4StartDate,
        caseEndDate: hours?.leaveCaseBank4EndDate,
        caseEffectiveHours: hours?.leaveCaseBank4EffectiveHours,
        caseUsedHours: hours?.leaveCaseBank4UsedHours,
        caseFutureHours: hours?.leaveCaseBank4FutureHours,
        leavesCount: hours?.bank4LeavesCount,
        drawCount: hours?.bank4DrawCount,
        leaveYearId: hours?.bank4LeaveYearID,
        leaveYearState: hours?.bank4LeaveYearState,
        leaveYearDuration: hours?.bank4LeaveYearDuration,
        leaveYearDurationTypeId: hours?.bank4LeaveYearDurationTypeID,
        leaveYearStartId: hours?.bank4LeaveYearStartID,
        startDate: hours?.bank4StartDate,
        endDate: hours?.bank4EndDate,
        employeeTotalHours: hours?.employeeBank4TotalHours,
        employeeUsedHours: hours?.employeeBank4UsedHours,
        employeeAdjustments: hours?.employeeBank4Adjustments,
        employeeAvailableHours: hours?.employeeBank4AvailableHours,
        employeeFutureHours: hours?.employeeBank4FutureHours
      },
      [Banks.Bank5]: {
        applicable: info?.isBank5HoursApplicable,
        restrictToFullDays: info?.bank5RestrictToFullDayIncrements,
        name: buckets?.bank5Name ?? buckets?.bank5DefaultName,
        caseStartDate: hours?.leaveCaseBank5StartDate,
        caseEndDate: hours?.leaveCaseBank5EndDate,
        caseEffectiveHours: hours?.leaveCaseBank5EffectiveHours,
        caseUsedHours: hours?.leaveCaseBank5UsedHours,
        caseFutureHours: hours?.leaveCaseBank5FutureHours,
        leavesCount: hours?.bank5LeavesCount,
        drawCount: hours?.bank5DrawCount,
        leaveYearId: hours?.bank5LeaveYearID,
        leaveYearState: hours?.bank5LeaveYearState,
        leaveYearDuration: hours?.bank5LeaveYearDuration,
        leaveYearDurationTypeId: hours?.bank5LeaveYearDurationTypeID,
        leaveYearStartId: hours?.bank5LeaveYearStartID,
        startDate: hours?.bank5StartDate,
        endDate: hours?.bank5EndDate,
        employeeTotalHours: hours?.employeeBank5TotalHours,
        employeeUsedHours: hours?.employeeBank5UsedHours,
        employeeAdjustments: hours?.employeeBank5Adjustments,
        employeeAvailableHours: hours?.employeeBank5AvailableHours,
        employeeFutureHours: hours?.employeeBank5FutureHours
      }
    };

    return Object.values(banks);
  }
}
